// Leer y escribir `.xlsx` sin librería de Excel —
// ADR-2026-09-08-el-agente-lee-y-escribe-planillas.
//
// Un xlsx es un ZIP con XMLs adentro, y `fflate` ya es dependencia (el zip de descargas,
// el export de Premiere). Lo que necesitamos es acotado —leer una grilla de celdas y
// escribir otra—, así que no entra SheetJS: el paquete `xlsx` de npm quedó congelado en
// 0.18.5 con un aviso de prototype pollution y las versiones nuevas no se publican ahí.
//
// Lo que SÍ se soporta al leer: varias hojas, shared strings, strings inline, números,
// booleanos, y FECHAS (que en xlsx son números y sin los estilos se leen como "45678").
// Lo que NO: fórmulas (se lee el último valor calculado, que es lo que el usuario ve),
// formato, colores, celdas combinadas. Para entender y transformar datos alcanza.

import { unzipSync, zipSync, strToU8, strFromU8 } from 'fflate';

export interface HojaPlanilla {
  nombre: string;
  /** Filas × columnas, todo como texto: es lo que el modelo lee y escribe. */
  filas: string[][];
}

// ── XML mínimo ──────────────────────────────────────────────────────────────

const desescapar = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&amp;/g, '&');

const escapar = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Los caracteres de control rompen el XML y Excel se niega a abrir el archivo
    // entero: un solo byte raro en una celda tira abajo el documento.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

const atributo = (tag: string, nombre: string): string => {
  const m = new RegExp(`${nombre}="([^"]*)"`).exec(tag);
  return m ? desescapar(m[1]) : '';
};

/** Índice 0-based de columna a partir de la referencia de celda ("B3" → 1). */
export function columnaDeRef(ref: string): number {
  const letras = /^([A-Z]+)/.exec(ref.toUpperCase())?.[1] || '';
  let n = 0;
  for (const c of letras) n = n * 26 + (c.charCodeAt(0) - 64);
  return Math.max(0, n - 1);
}

/** Lo inverso: 0 → "A", 26 → "AA". */
export function refDeColumna(indice: number): string {
  let n = Math.max(0, Math.floor(indice)) + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

// ── Fechas ──────────────────────────────────────────────────────────────────

/** Formatos de fecha/hora que Excel trae de fábrica. */
const NUMFMT_FECHA = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47]);

/**
 * Serial de Excel → `YYYY-MM-DD` (o con hora si la tiene). El día 0 es el 1899-12-30 y no
 * el 31: Excel arrastra un 1900 bisiesto que nunca existió, y ese corrimiento es el que
 * hace que la fecha salga un día antes si se ignora.
 */
export function fechaDeSerial(serial: number): string {
  const dias = Math.floor(serial);
  const resto = serial - dias;
  const ms = Date.UTC(1899, 11, 30) + dias * 86_400_000 + Math.round(resto * 86_400_000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(serial);
  const iso = d.toISOString();
  return resto > 0 ? iso.slice(0, 16).replace('T', ' ') : iso.slice(0, 10);
}

/** Índices de estilo (`s="N"`) que corresponden a una fecha, según styles.xml. */
function estilosDeFecha(stylesXml: string | null): Set<number> {
  const out = new Set<number>();
  if (!stylesXml) return out;
  // Formatos propios del archivo: son fecha si la máscara tiene y/m/d fuera de comillas.
  const propios = new Map<number, boolean>();
  for (const m of stylesXml.matchAll(/<numFmt\b[^>]*\/?>/g)) {
    const id = Number(atributo(m[0], 'numFmtId'));
    const code = atributo(m[0], 'formatCode').replace(/"[^"]*"/g, '');
    if (Number.isFinite(id)) propios.set(id, /[ymdhs]/i.test(code));
  }
  const xfs = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(stylesXml)?.[1] ?? '';
  let i = 0;
  for (const m of xfs.matchAll(/<xf\b[^>]*\/?>/g)) {
    const id = Number(atributo(m[0], 'numFmtId'));
    if (NUMFMT_FECHA.has(id) || propios.get(id)) out.add(i);
    i += 1;
  }
  return out;
}

// ── Leer ────────────────────────────────────────────────────────────────────

function sharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const out: string[] = [];
  for (const si of xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)) {
    // Un `si` puede venir partido en varios `<t>` (texto con formatos mezclados).
    let texto = '';
    for (const t of si[1].matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) texto += desescapar(t[1]);
    out.push(texto);
  }
  return out;
}

function filasDeHoja(xml: string, compartidas: string[], fechas: Set<number>): string[][] {
  const filas: string[][] = [];
  for (const filaM of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
    const nro = Number(atributo(`<row ${filaM[1]}>`, 'r'));
    const fila: string[] = [];
    for (const celdaM of filaM[2].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = `<c ${celdaM[1]}>`;
      const ref = atributo(attrs, 'r');
      const tipo = atributo(attrs, 't');
      const estilo = Number(atributo(attrs, 's'));
      const cuerpo = celdaM[2] ?? '';
      let valor = '';
      if (tipo === 'inlineStr') {
        for (const t of cuerpo.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)) valor += desescapar(t[1]);
      } else {
        const v = /<v\b[^>]*>([\s\S]*?)<\/v>/.exec(cuerpo)?.[1] ?? '';
        const crudo = desescapar(v);
        if (tipo === 's') valor = compartidas[Number(crudo)] ?? '';
        else if (tipo === 'b') valor = crudo === '1' ? 'VERDADERO' : 'FALSO';
        else if (tipo === 'e') valor = crudo; // #N/A, #REF!… se muestran como están
        else if (crudo !== '' && fechas.has(estilo) && Number.isFinite(Number(crudo)))
          valor = fechaDeSerial(Number(crudo));
        else valor = crudo;
      }
      // La celda va en SU columna: una fila que empieza en C deja A y B vacías, y sin
      // esto todo el resto de la fila queda corrido.
      const col = ref ? columnaDeRef(ref) : fila.length;
      while (fila.length < col) fila.push('');
      fila[col] = valor;
    }
    // Lo mismo con las filas salteadas (`r` da el número real, 1-based).
    if (Number.isFinite(nro)) while (filas.length < nro - 1) filas.push([]);
    filas.push(fila);
  }
  return filas;
}

/** Un `.xlsx` en bytes → sus hojas. Tira `Error` con mensaje legible si no es un xlsx. */
export function leerXlsx(bytes: Uint8Array): HojaPlanilla[] {
  let archivos: Record<string, Uint8Array>;
  try {
    archivos = unzipSync(bytes);
  } catch {
    throw new Error('Ese archivo no parece un Excel válido.');
  }
  const leer = (ruta: string): string | null =>
    archivos[ruta] ? strFromU8(archivos[ruta]) : null;

  const workbook = leer('xl/workbook.xml');
  if (!workbook) throw new Error('Ese archivo no parece un Excel válido.');
  const compartidas = sharedStrings(leer('xl/sharedStrings.xml'));
  const fechas = estilosDeFecha(leer('xl/styles.xml'));

  // r:id → ruta real de la hoja.
  const rels = new Map<string, string>();
  for (const m of (leer('xl/_rels/workbook.xml.rels') ?? '').matchAll(
    /<Relationship\b[^>]*\/?>/g,
  )) {
    const destino = atributo(m[0], 'Target')
      .replace(/^\/?xl\//, '')
      .replace(/^\//, '');
    rels.set(atributo(m[0], 'Id'), destino);
  }

  const hojas: HojaPlanilla[] = [];
  let n = 0;
  for (const m of workbook.matchAll(/<sheet\b[^>]*\/?>/g)) {
    n += 1;
    const nombre = atributo(m[0], 'name') || `Hoja ${n}`;
    const rid = atributo(m[0], 'r:id') || atributo(m[0], 'id');
    const ruta = rels.get(rid);
    const xml = (ruta ? leer(`xl/${ruta}`) : null) ?? leer(`xl/worksheets/sheet${n}.xml`);
    if (!xml) continue;
    hojas.push({ nombre, filas: filasDeHoja(xml, compartidas, fechas) });
  }
  if (hojas.length === 0) throw new Error('El Excel no tiene ninguna hoja con datos.');
  return hojas;
}

// ── Escribir ────────────────────────────────────────────────────────────────

/** Un valor que Excel debe tratar como NÚMERO. Se excluye lo que "parece" número pero no
 *  lo es: un código de barras con ceros adelante o un teléfono con + tienen que quedar
 *  como texto, o Excel les come los ceros. */
const esNumero = (v: string): boolean =>
  /^-?\d+(\.\d+)?$/.test(v) && !/^-?0\d/.test(v) && Math.abs(Number(v)) < 1e15;

function hojaAXml(hoja: HojaPlanilla): string {
  const filas = hoja.filas
    .map((fila, f) => {
      const celdas = fila
        .map((valor, c) => {
          const v = valor ?? '';
          if (v === '') return '';
          const ref = `${refDeColumna(c)}${f + 1}`;
          return esNumero(v)
            ? `<c r="${ref}"><v>${v}</v></c>`
            : `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapar(v)}</t></is></c>`;
        })
        .join('');
      return `<row r="${f + 1}">${celdas}</row>`;
    })
    .join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${filas}</sheetData></worksheet>`;
}

/** Nombre de hoja válido para Excel: sin `[]:*?/\`, sin exceder 31 caracteres. */
export function nombreDeHojaValido(nombre: string, indice: number): string {
  const limpio = (nombre || '')
    .replace(/[[\]:*?/\\]/g, ' ')
    .trim()
    .slice(0, 31);
  return limpio || `Hoja ${indice + 1}`;
}

/** Hojas → bytes de un `.xlsx`. Se escriben strings INLINE (sin sharedStrings): un
 *  archivo más grande, pero mucho menos código y sin tabla que pueda desincronizarse. */
export function escribirXlsx(hojas: HojaPlanilla[]): Uint8Array {
  const usadas = hojas.length > 0 ? hojas : [{ nombre: 'Hoja 1', filas: [] }];
  const nombres = usadas.map((h, i) => nombreDeHojaValido(h.nombre, i));
  const archivos: Record<string, Uint8Array> = {};

  archivos['[Content_Types].xml'] = strToU8(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>${usadas
      .map(
        (_, i) =>
          `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
      )
      .join('')}</Types>`,
  );
  archivos['_rels/.rels'] = strToU8(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
  );
  archivos['xl/workbook.xml'] = strToU8(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${nombres
      .map((n, i) => `<sheet name="${escapar(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
      .join('')}</sheets></workbook>`,
  );
  archivos['xl/_rels/workbook.xml.rels'] = strToU8(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${usadas
      .map(
        (_, i) =>
          `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
      )
      .join('')}</Relationships>`,
  );
  usadas.forEach((h, i) => {
    archivos[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(hojaAXml(h));
  });
  return zipSync(archivos);
}
